"use client";

import { LightbulbIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { CheckIcon } from "../CheckIcon";
import { ChoiceBox } from "../ChoiceBox";
import { OrderSummaryCard } from "../OrderSummaryCard";
import {
  ADDRESS_OPTIONS,
  type AddressOption,
  type PackageSummary,
} from "../pricing";

const BENEFITS = ["Central Hong Kong address", "Privacy protection", "Digital mail scanning"];

const INCLUDED = [
  "Hong Kong local registered office address (legal requirement)",
  "Secure digital mailroom access anytime",
  "Mail scanning and upload within 7 days",
  "Government and bank mail received on your behalf",
];

/**
 * The "What's included" list uses white marks rather than the blue check badge
 * used elsewhere (the export fills these with White_Color). Drawn as a plain
 * tick since a white knocked-out badge would show the page through the tick.
 */
function WhiteCheck() {
  return (
    <svg viewBox="0 0 24 24" className="mt-[3px] h-6 w-6 shrink-0" aria-hidden="true">
      <path
        d="M5 12.5l4.5 4.5L19 7"
        fill="none"
        stroke="#ffffff"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PriceChip({ option }: { option: AddressOption }) {
  return (
    <span
      className={cn(
        "relative flex h-8 items-center overflow-hidden rounded-lg px-4 font-inter text-base font-medium text-white",
        option.chipGreen && "bg-[#28B446]",
      )}
    >
      {/* 51px white glow behind the label, per the design */}
      <span
        aria-hidden
        className="pointer-events-none absolute -right-4 top-0 h-[51px] w-[51px] rounded-full bg-white blur-[72px]"
      />
      <span className="relative">{option.priceLabel}</span>
    </span>
  );
}

/**
 * Shared by both branches, whose address screens are identical down to the
 * copy, the two priced options and the map — the only difference is the width
 * of the Next pill.
 */
export function RegisteredAddressStep({
  addressId,
  onAddressChange,
  summary,
  onNext,
  wideNext = false,
}: {
  addressId: string | null;
  onAddressChange: (id: string) => void;
  summary: PackageSummary;
  onNext: () => void;
  /** The manage design's 388px Next, against the register branch's 188px. */
  wideNext?: boolean;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (addressId) onNext();
      }}
      className="mx-auto w-full max-w-[1250px] px-4 pb-16 pt-8 sm:px-6 sm:pb-24 sm:pt-12 lg:pt-[31px]"
    >
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        {/* Left column. items-end only from lg — below that it would push the
            non-stretching children to the right of everything else. */}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-6 sm:gap-8 lg:items-end">
          <h1 className="w-full font-suisse-intl-bold text-[26px] leading-tight text-white sm:text-4xl md:text-5xl lg:text-[54px] lg:leading-[71px]">
            Add your Hong Kong registered address
          </h1>

          <div className="w-full">
            <p className="font-suisse-intl-medium text-base leading-relaxed text-white sm:text-lg sm:leading-[28.8px]">
              Every Hong Kong company needs a registered office address.
              <br />
              Use our professional address service or manage your own.
            </p>
            <h2 className="mt-5 font-suisse-intl-bold text-xl leading-snug text-white sm:mt-[28.8px] sm:text-2xl sm:leading-[38.4px]">
              I need an address
            </h2>
          </div>

          <ul className="flex w-full flex-col items-start">
            {BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-start gap-2">
                <CheckIcon />
                <span className="font-suisse-intl text-base leading-7 text-white sm:text-lg sm:leading-[28.8px]">
                  {benefit}
                </span>
              </li>
            ))}
          </ul>

          {/* Founder tip callout */}
          <div className="flex w-full flex-col items-start gap-3 rounded-2xl bg-[#FFD400]/10 p-4 outline outline-1 -outline-offset-1 outline-[#FFD400] sm:gap-4">
            <div className="flex items-center gap-2">
              <LightbulbIcon aria-hidden className="h-6 w-6 shrink-0 text-white" />
              <span className="font-suisse-intl text-base leading-snug text-white sm:text-lg sm:leading-[28.8px]">
                Founder tip
              </span>
            </div>
            <p className="font-suisse-intl text-sm leading-[22.4px] text-white">
              Your registered address is publicly visible. Using a business address helps protect
              your privacy.
            </p>
          </div>

          {/* Address choice. Side by side only from lg: these labels are full
              sentences, and two columns at 640px leave ~230px each, which turns
              them into very tall, narrow cards. */}
          <fieldset className="flex w-full flex-col gap-6 sm:gap-8 lg:flex-row lg:items-stretch">
            <legend className="sr-only">Do you need a registered address?</legend>
            {ADDRESS_OPTIONS.map((option) => {
              const selected = addressId === option.id;

              return (
                <label
                  key={option.id}
                  className={cn(
                    "flex flex-1 cursor-pointer flex-col items-end justify-between gap-6 rounded-[28px] bg-[#0F1319] p-6 outline outline-2 -outline-offset-2 transition-colors sm:gap-8 sm:rounded-[32px] sm:p-8",
                    selected ? "outline-[#58A6FF]" : "outline-transparent hover:outline-white/20",
                  )}
                >
                  <div className="flex w-full items-start gap-2">
                    <input
                      type="radio"
                      name="registered-address"
                      value={option.id}
                      checked={selected}
                      onChange={() => onAddressChange(option.id)}
                      className="peer sr-only"
                    />
                    <ChoiceBox selected={selected} />
                    <span className="flex-1 font-suisse-intl text-base leading-7 text-white sm:text-lg sm:leading-[28.8px]">
                      {option.label}
                    </span>
                  </div>
                  <PriceChip option={option} />
                </label>
              );
            })}
          </fieldset>

          {/* Full width on phones; the design's right-aligned pill from lg,
              where items-end applies. */}
          <button
            type="submit"
            disabled={!addressId}
            className={cn(
              "flex h-14 w-full items-center justify-center rounded-full bg-white px-4 font-suisse-intl text-xl leading-5 text-[#2A4775] transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50",
              wideNext ? "lg:max-w-[388px]" : "lg:max-w-[188px]",
            )}
          >
            Next
          </button>

          {/* What's included — sits below the Next button, per the design */}
          <div className="flex w-full flex-col items-start gap-5 sm:gap-6">
            <h2 className="font-suisse-intl-bold text-xl leading-snug text-white sm:text-2xl sm:leading-6">
              What&apos;s included
            </h2>
            <div className="flex w-full flex-col items-center gap-6 sm:gap-8 lg:flex-row">
              <ul className="flex flex-1 flex-col items-start gap-2">
                {INCLUDED.map((item) => (
                  <li key={item} className="flex w-full items-start gap-2">
                    <WhiteCheck />
                    <span className="flex-1 font-suisse-intl text-base leading-7 text-white sm:text-lg sm:leading-[28.8px]">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
              {/* Fluid below sm: a fixed 304px overflows a 320px screen once
                  the page's 16px padding is taken off. */}
              <img
                src="/get-started/map.svg"
                alt=""
                className="h-auto w-full max-w-[304px] rounded-lg sm:h-[222px] sm:w-[304px] sm:shrink-0"
              />
            </div>
          </div>
        </div>

        {/* Right column — order summary */}
        <OrderSummaryCard summary={summary} className="lg:w-[402px] lg:shrink-0" />
      </div>
    </form>
  );
}
